import { ParsedUrlQuery } from "node:querystring"
import { NextRouter } from "next/router"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import { trim } from "./string"
import { extractKeys } from "./object"
import { resetErrorCounter } from "./error-counter"

const routePathsV3 = [
    "",
    "category",
    "suite",
    "guests-info",
    "send-quote",
    "send-quote-thank-you",
    "payment",
    "thank-you",
    "modal-quote-thank-you",
    "thank-you-courtesy-hold",
] as const
export type RoutePathV3 = (typeof routePathsV3)[number]
export type RouteV3 = { basePath: "/v3/[cruiseCode]"; paths: readonly RoutePathV3[]; path: RoutePathV3 }
type AllRoutesV3 = { basePath: "/v3/[cruiseCode]"; paths: readonly RoutePathV3[] }

const routePathsV4 = [
    "",
    "category",
    "fare",
    "guests-info",
    "payment",
    "thank-you",
    "modal-quote-thank-you",
    "thank-you-courtesy-hold",
    "send-quote",
    "send-quote-thank-you",
] as const
export type RoutePathV4 = (typeof routePathsV4)[number]
export type RouteV4 = { basePath: "/v4/[cruiseCode]"; paths: readonly RoutePathV4[]; path: RoutePathV4 }
type AllRoutesV4 = { basePath: "/v4/[cruiseCode]"; paths: readonly RoutePathV4[] }

type Route = RouteV3 | RouteV4
type AllRoutes = AllRoutesV3 | AllRoutesV4

export const routeV3 = (path: RoutePathV3): RouteV3 => ({ basePath: "/v3/[cruiseCode]", paths: routePathsV3, path })
export const allRoutesV3 = (): AllRoutesV3 => ({ basePath: "/v3/[cruiseCode]", paths: routePathsV3 })
export const homeV3 = (cruiseCode: string): string => `/v3/${cruiseCode}`

export const isRoutePathV3 = (path: string): path is RoutePathV3 => routePathsV3.includes(path as RoutePathV3)
export const isRouteV3 = (router: BookingRouter, path: RoutePathV3): boolean => router.pathname.includes(path)

export const routeV4 = (path: RoutePathV4): RouteV4 => ({ basePath: "/v4/[cruiseCode]", paths: routePathsV4, path })
export const allRoutesV4 = (): AllRoutesV4 => ({ basePath: "/v4/[cruiseCode]", paths: routePathsV4 })
export const homeV4 = (cruiseCode: string): string => `/v4/${cruiseCode}`

export const isRoutePathV4 = (path: string): path is RoutePathV4 => routePathsV4.includes(path as RoutePathV4)
export const isRouteV4 = (router: BookingRouter, path: RoutePathV4): boolean => router.pathname.includes(path)

export async function goto(router: BookingRouter, route: Route): Promise<boolean> {
    const currentPath = currentPathOrDefault(router, route, "")
    if (currentPath == route.path) return Promise.resolve(true)

    resetErrorCounter()

    return router.push({
        pathname: `${route.basePath}/${route.path}`,
        query: router.query,
    })
}

export async function gotoWithoutQuery(router: BookingRouter, route: Route): Promise<boolean> {
    return router.push({
        pathname: `${route.basePath}/${route.path}`,
        query: removeAllParams(router.query),
    })
}

export function currentPathOrDefault<T extends AllRoutes>(
    router: BookingRouter,
    route: T,
    defaultRoute: T["paths"][0],
): T["paths"][0] {
    type TReturn = T["paths"][0]
    const pathname = router.pathname
    const withoutBasePath = pathname.replace(route.basePath, "")
    const path = trim("/")(withoutBasePath)

    const exists = route.paths.some((x) => x == path)
    return exists ? (path as TReturn) : defaultRoute
}

const persistentQueryParams = ["cruiseCode", "country", "currency"] as const
const normalQueryParams = [
    "fare",
    "suite",
    "suite-number",
    "guests",
    "airfare",
    "gateway",
    "vs",
    "checkout",
    "modal",
] as const
const transientQueryParams = ["is-redirect"] as const
type PersistentQueryParam = (typeof persistentQueryParams)[number]
export type NormalQueryParam = (typeof normalQueryParams)[number]
export type TransientQueryParam = (typeof transientQueryParams)[number]
type AllQueryParam = PersistentQueryParam | NormalQueryParam | TransientQueryParam

export type BookingRouter = Pick<NextRouter, "pathname" | "query" | "push" | "replace">

export function removeAllParams(query: ParsedUrlQuery): ParsedUrlQuery {
    const newQuery = { ...query }
    normalQueryParams.forEach((key) => delete newQuery[key])

    return newQuery
}

export async function updateQuery(
    router: Pick<BookingRouter, "pathname" | "push">,
    query: ParsedUrlQuery,
): Promise<boolean> {
    const url = {
        pathname: router.pathname,
        query: query,
    }
    return await router.push(url, undefined, { shallow: true })
}

export async function replaceQuery(router: BookingRouter, query: ParsedUrlQuery): Promise<boolean> {
    const url = {
        pathname: router.pathname,
        query: query,
    }
    return await router.replace(url, undefined, { shallow: true })
}

export function removeFromQuery(query: ParsedUrlQuery, ...paramNames: AllQueryParam[]): ParsedUrlQuery {
    return F.pipe(
        Object.entries(query),
        ROA.filter(([key]) => !paramNames.includes(key as AllQueryParam)),
        Object.fromEntries,
    )
}

export async function updateUrl(router: Pick<BookingRouter, "push">, newUrl: URL) {
    return await router.push(newUrl.href, undefined, { shallow: true })
}

export async function replaceUrl(router: Pick<BookingRouter, "replace">, newUrl: URL) {
    return await router.replace(newUrl.href, undefined, { shallow: true })
}

export function addQueryParam(url: string, paramName: NormalQueryParam, paramValue: number | string) {
    const newUrl = new URL(url)
    newUrl.searchParams.set(paramName, paramValue.toString())
    return newUrl
}

export function removeQueryParam(url: string, ...paramNames: NormalQueryParam[]) {
    const newUrl = new URL(url)
    paramNames.forEach((x) => newUrl.searchParams.delete(x))
    return newUrl
}

export function appendQueryParam(url: string, paramName: NormalQueryParam, paramValue: number | string) {
    const newUrl = new URL(url)
    const isAlreadyPresent = newUrl.searchParams.getAll(paramName).some((value) => value === paramValue.toString())
    if (isAlreadyPresent) return newUrl

    newUrl.searchParams.append(paramName, paramValue.toString())
    return newUrl
}

export function getQueryParam(url: string, paramName: NormalQueryParam) {
    const newUrl = new URL(url)
    return newUrl.searchParams.get(paramName) || ""
}

export function getAllQueryParam(url: string | undefined, paramName: NormalQueryParam) {
    if (!url) return []
    const newUrl = new URL(url)
    return newUrl.searchParams.getAll(paramName)
}

export function getFromQuery(router: BookingRouter, key: AllQueryParam): string | undefined {
    return F.pipe(
        O.fromNullable(router.query[key]),
        O.fold(
            () => undefined,
            (x) => x.toString(),
        ),
    )
}

export const getFromNormalQuery = (router: BookingRouter, key: NormalQueryParam): string | undefined =>
    getFromQuery(router, key)

export const extractNormalQueryParams = (query: NextRouter["query"]) => extractKeys(normalQueryParams)(query)

export const dotComURL = (path: string) => `https://www.silversea.com${path}`
